import { Check, Eye, EyeOff, Reply as ReplyIcon, ThumbsUp, Trash2 } from 'lucide-react';
import type { InboxChannel, InboxStatus } from '../../types/inbox';
import type { InboxMessageView, PostGroupEntryView } from '../../types/inboxView';
import { Badge } from '../../components/ui/badge';
import { MessageAttachments } from '../../components/social/MessageAttachments';
import { cn } from '../../lib/utils';
import { formatRelativeTime } from '../../lib/relativeTime';
import { canLikeComment, CHANNEL_LABEL, formatExactTimestamp } from '../../lib/inboxView';
import { commentModerationOffered } from '../../lib/inboxRowActions';
import type { CommentNode } from '../../lib/commentTree';

// ONE comment of a post's conversation, and the message rows inside it. Pulled out
// of ThreadDetail.tsx so the club portal can host the SAME renderer (the inbox is
// one feature on two surfaces; the staff page is not the portal's dependency).
//
// ⚠️ PURELY PRESENTATIONAL AND CALLBACK-INJECTED: it reaches for no API and no
// current-user hook, and it is typed against the VIEW types (src/types/inboxView.ts),
// never the stored doc, so a club's projection satisfies it without a cast.
// Which actions run, and which are confirmed first, is the host's decision.

const COMMENT_STATUS_STYLES: Record<InboxStatus, string> = {
  new: 'bg-kyck-50 text-kyck-700 border-kyck-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-slate-100 text-slate-500 border-slate-200',
};

/**
 * Part B — ONE comment in the post group: its own resolvable item (status badge
 * + inline resolve tick), indented under its parent for context, with the
 * comment body + any replies WE sent beneath it. Clicking it makes it the
 * active reply/resolve target.
 */
export function PostGroupComment({
  node,
  selected,
  isReplyTarget,
  onSelect,
  onReply,
  onResolve,
  resolving,
  likePending,
  onLike,
  onHide,
  onUnhide,
  onDelete,
  priorityFlag,
}: {
  node: CommentNode<PostGroupEntryView>;
  selected: boolean;
  // B2 — whether the composer is currently aimed at THIS comment.
  isReplyTarget: boolean;
  onSelect: () => void;
  onReply: (m: InboxMessageView) => void;
  onResolve: () => void;
  resolving: boolean;
  likePending: boolean;
  onLike: (m: InboxMessageView) => void;
  onHide: (m: InboxMessageView) => void;
  onUnhide: (m: InboxMessageView) => void;
  onDelete: (m: InboxMessageView) => void;
  /** Staff only: the thread's priority flag. A club has no such flag and passes nothing. */
  priorityFlag?: boolean;
}) {
  const { thread, messages } = node.entry;
  const resolved = thread.status === 'resolved';
  // One of OUR OWN top-level comments (2026-07-19). It's shown for context but
  // isn't an actionable item — no status, no priority, no resolve tick, and it
  // can't be made the reply target (you reply to customers, not yourself).
  const owned = thread.ownerAuthored === true;
  // Hide / Delete only where the platform has comments to moderate. One definition,
  // shared with the server routes that refuse the action for any other channel.
  const moderated = commentModerationOffered(thread.channel, thread.kind);
  return (
    <div
      style={{ marginLeft: `${node.depth * 1.5}rem` }}
      className={cn(
        'rounded-lg border p-2 transition-colors',
        owned
          ? 'border-kyck-200 bg-kyck-50/40'
          : selected
            ? 'border-kyck-300 bg-kyck-50/50'
            : 'border-slate-200 hover:border-slate-300',
        // B2 — a ring shows which comment the composer is aimed at, when that
        // differs from the selected one.
        isReplyTarget && !selected && !owned && 'ring-1 ring-kyck-300',
        resolved && !selected && !owned && 'opacity-60',
      )}
    >
      <div className="mb-1 flex items-center gap-2">
        {owned ? (
          <span className="min-w-0 flex-1 truncate text-xs font-semibold text-kyck-800">
            {thread.author.name}
          </span>
        ) : (
          <button
            type="button"
            onClick={onSelect}
            className="min-w-0 flex-1 truncate text-left text-xs font-semibold text-slate-700 hover:text-kyck-700"
            title="Make this the active comment (reply / resolve)"
          >
            {thread.author.name}
          </button>
        )}
        {owned && (
          <Badge
            variant="outline"
            className="border-kyck-200 bg-kyck-50 px-1.5 py-0 text-[10px] text-kyck-700"
          >
            Your comment
          </Badge>
        )}
        {!owned && priorityFlag === true && (
          <Badge variant="outline" className="border-amber-300 bg-amber-50 px-1 py-0 text-[10px] text-amber-700">
            Priority
          </Badge>
        )}
        {!owned && (
          <Badge
            variant="outline"
            className={cn('px-1.5 py-0 text-[10px]', COMMENT_STATUS_STYLES[thread.status])}
          >
            {thread.status}
          </Badge>
        )}
        {/* Inline per-comment resolve tick (Part B item 4) — resolve
            several-but-not-all without touching the rest of the post. */}
        {!resolved && !owned && (
          <button
            type="button"
            onClick={onResolve}
            disabled={resolving}
            title="Resolve this comment"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:border-kyck-300 hover:text-kyck-600 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="space-y-2">
        {messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            channel={thread.channel}
            moderated={moderated}
            likePending={likePending}
            onLike={() => onLike(message)}
            onReply={() => onReply(message)}
            onHide={() => onHide(message)}
            onUnhide={() => onUnhide(message)}
            onDelete={() => onDelete(message)}
          />
        ))}
        {messages.length === 0 && (
          <p className="px-1 py-2 text-xs italic text-slate-400">Comment text unavailable.</p>
        )}
      </div>
    </div>
  );
}

function MessageRow({
  message,
  channel,
  moderated,
  likePending,
  onLike,
  onReply,
  onHide,
  onUnhide,
  onDelete,
}: {
  message: InboxMessageView;
  channel: InboxChannel;
  moderated: boolean;
  likePending: boolean;
  onLike: () => void;
  onReply: () => void;
  onHide: () => void;
  onUnhide: () => void;
  onDelete: () => void;
}) {
  const incoming = message.direction === 'incoming';
  const canModerate = incoming && message.externalId !== null;
  const liked = message.liked ?? false;
  // A2 — Instagram comments cannot be liked via Zernio (it 502'd); hide the
  // control rather than render a button that errors.
  const likeable = canLikeComment(message, channel);
  // Part F — per-message hidden state (was wrongly read from the thread).
  const hidden = message.hidden === true;
  // A3 — a media-only comment (e.g. an IG GIF) arrives with no text AND no
  // attachment, because Zernio exposes comment attachments for Facebook only.
  // Render an explicit placeholder instead of an empty bubble.
  const hasRenderableBody =
    message.text.trim() !== '' || message.attachments.some((a) => Boolean(a.url));
  return (
    <div className={cn('group flex', incoming ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-3xl rounded-lg px-3 py-2',
          incoming ? 'bg-slate-100 text-slate-800' : 'bg-kyck-50 text-slate-800',
        )}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold text-slate-600">{message.authorName}</span>
          {message.isPrivateReply && (
            <Badge variant="outline" className="px-1 py-0 text-[10px]">
              Private
            </Badge>
          )}
          {hidden && (
            <Badge variant="outline" className="px-1 py-0 text-[10px] text-slate-500">
              Hidden
            </Badge>
          )}
          <span
            className="text-[11px] text-slate-400"
            title={formatRelativeTime(message.createdAt)}
          >
            {formatExactTimestamp(message.createdAt)}
          </span>
          {typeof message.reactionCount === 'number' && message.reactionCount > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[11px] text-slate-400"
              title="Reactions as of the last sync (not live)"
            >
              <ThumbsUp className="h-3 w-3" />
              {message.reactionCount}
            </span>
          )}
        </div>
        {hasRenderableBody ? (
          <p className="mt-0.5 whitespace-pre-wrap text-sm">{message.text}</p>
        ) : (
          <p className="mt-0.5 text-sm italic text-slate-400">
            Media-only comment (GIF/sticker) — {CHANNEL_LABEL[channel]} doesn't expose the media
            here.{' '}
            {message.externalId && (
              <span className="not-italic">Open the post to view it.</span>
            )}
          </p>
        )}
        <MessageAttachments attachments={message.attachments} />
        {canModerate && (
          <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-400">
            {likeable && (
              <button
                type="button"
                onClick={onLike}
                disabled={likePending}
                title={liked ? 'Unlike this comment' : 'Like this comment as the Page'}
                className={cn(
                  'inline-flex items-center gap-1 hover:text-kyck-700',
                  liked && 'font-medium text-kyck-700',
                )}
              >
                <ThumbsUp className={cn('h-3.5 w-3.5', liked && 'fill-current')} />
                {liked ? 'Liked' : 'Like'}
              </button>
            )}
            {/* B2 — restored: aim the composer at THIS comment without leaving
                the post (you often arrive from one comment and need to answer
                another in the same thread). */}
            <button
              type="button"
              onClick={onReply}
              title="Reply to this comment"
              className="inline-flex items-center gap-1 hover:text-kyck-700"
            >
              <ReplyIcon className="h-3.5 w-3.5" />
              Reply
            </button>
            {moderated && (hidden ? (
              <button
                type="button"
                onClick={onUnhide}
                title="Unhide this comment"
                className="inline-flex items-center gap-1 hover:text-slate-700"
              >
                <Eye className="h-3.5 w-3.5" />
                Unhide
              </button>
            ) : (
              <button
                type="button"
                onClick={onHide}
                title="Hide this comment"
                className="inline-flex items-center gap-1 hover:text-slate-700"
              >
                <EyeOff className="h-3.5 w-3.5" />
                Hide
              </button>
            ))}
            {moderated && (
            <button
              type="button"
              onClick={onDelete}
              title="Delete this comment"
              className="inline-flex items-center gap-1 hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
